import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {UserIcon} from '../../components/icons';
import createAxiosInstance from '../../support/AxiosSupport';
import Spinner from '../../components/Spinner';
import authService from '../../service/AuthService';
import {useTheme} from '../../hooks/useTheme';
import ProfilePlanCard from '../../components/ProfilePlanCard';
import {invalidateSubscriptionCache} from '../../service/subscriptionCache';

interface UserInfo {
    name?: string;
    preferred_username?: string;
    email?: string;
    given_name?: string;
    family_name?: string;
}

const ProfileInfoPage: React.FC = () => {
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [editing, setEditing] = useState(false);
    const [displayName, setDisplayName] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const {theme, toggleTheme} = useTheme();

    useEffect(() => {
        (async () => {
            if (!authService.getUserInfo()?.preferred_username) {
                await authService.loadUserInfo();
            }
            setUserInfo(authService.getUserInfo());
        })();
    }, []);

    const handleLogout = async () => {
        // Кеш подписки — состояние аккаунта: следующий вход в той же вкладке не должен
        // увидеть тариф предыдущего пользователя (requirements SEC-5).
        invalidateSubscriptionCache();
        await authService.logout();
        window.location.href = '/login';
    };

    const startEditing = () => {
        setDisplayName(userInfo?.given_name || userInfo?.name || '');
        setSaveError('');
        setEditing(true);
    };

    /** Отмена возвращает прежнее имя и убирает ошибку — она относилась к брошенной попытке. */
    const cancelEditing = () => {
        setSaveError('');
        setEditing(false);
    };

    const saveDisplayName = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = displayName.trim();
        // Пробелы «проходят» атрибут required, а сервер их отвергнет — говорим сразу.
        if (!trimmed) {
            setSaveError('Укажите, как к вам обращаться.');
            return;
        }
        setSaving(true);
        setSaveError('');
        try {
            const api = await createAxiosInstance();
            await api.patch('/account/profile', {displayName: trimmed});
            // Обновляем локальную копию профиля, чтобы имя сменилось сразу, а не после
            // следующего входа: claim'ы в текущем токене остаются прежними.
            await authService.loadUserInfo();
            setUserInfo(authService.getUserInfo());
            setEditing(false);
        } catch (err: unknown) {
            const description = (err as {response?: {data?: {description?: string}}})?.response?.data?.description;
            setSaveError(description || 'Не удалось сохранить имя. Попробуйте позже.');
        } finally {
            setSaving(false);
        }
    };

    if (!userInfo) return <Spinner/>;

    return (
        <div className="space-y-6">
            <div className="card p-6 flex flex-col sm:flex-row items-center gap-6">
                <div className="w-20 h-20 bg-brand-600 rounded-full flex items-center justify-center shrink-0">
                    <UserIcon className="w-10 h-10 text-white"/>
                </div>
                <div className="flex-1 min-w-0 text-center sm:text-left">
                    {editing ? (
                        <form onSubmit={saveDisplayName} data-testid="profile-info-form"
                              className="flex flex-col sm:flex-row gap-2 sm:items-center">
                            <label htmlFor="displayName" className="sr-only">Как к вам обращаться</label>
                            <input
                                id="displayName"
                                data-testid="displayName"
                                type="text"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                className="input-field h-11 sm:max-w-xs"
                                placeholder="Как к вам обращаться"
                                maxLength={100}
                                required
                                autoFocus
                                aria-invalid={!!saveError}
                                aria-describedby={saveError ? 'displayName-error' : undefined}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                        e.preventDefault();
                                        cancelEditing();
                                    }
                                }}
                            />
                            <div className="flex gap-2">
                                <button type="submit" data-testid="profile-info-save"
                                        className="btn-primary h-11" disabled={saving}>
                                    {saving ? 'Сохраняем...' : 'Сохранить'}
                                </button>
                                <button type="button" data-testid="profile-info-cancel"
                                        className="btn-secondary h-11" onClick={cancelEditing}>
                                    Отмена
                                </button>
                            </div>
                        </form>
                    ) : (
                        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate">
                            {userInfo.name || userInfo.preferred_username || 'Профиль'}
                        </h2>
                    )}
                    {saveError && (
                        <p id="displayName-error" role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">{saveError}</p>
                    )}
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{userInfo.email || ''}</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500">@{userInfo.preferred_username || ''}</p>
                </div>
                {!editing && (
                    <button type="button" data-testid="profile-info-edit"
                            className="btn-secondary shrink-0" onClick={startEditing}>
                        Редактировать профиль
                    </button>
                )}
            </div>

            {/* Тариф — только на xs/sm: на md+ он виден в дропдауне UserAvatar. */}
            <ProfilePlanCard/>

            <div className="card p-6">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Основная информация
                </h3>
                <div className="space-y-0 divide-y divide-gray-100 dark:divide-gray-700">
                    {[
                        {label: 'Имя',       value: userInfo.given_name},
                        {label: 'Фамилия',   value: userInfo.family_name},
                        {label: 'Логин',     value: userInfo.preferred_username},
                        {label: 'Email',     value: userInfo.email},
                    ].map(row => (
                        <div key={row.label} className="flex justify-between py-3">
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{row.label}</span>
                            <span className="text-sm text-gray-900 dark:text-gray-100">{row.value || '—'}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="card p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">Тёмная тема</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Переключить оформление интерфейса</p>
                    </div>
                    <button
                        onClick={toggleTheme}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
                            theme === 'dark' ? 'bg-brand-600' : 'bg-gray-200'
                        }`}
                        aria-checked={theme === 'dark'}
                        role="switch"
                        aria-label="Тёмная тема"
                    >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${theme === 'dark' ? 'translate-x-6' : 'translate-x-1'}`}/>
                    </button>
                </div>
            </div>

            <div className="pt-2">
                <button onClick={handleLogout} className="btn-danger px-6">
                    Выйти из системы
                </button>
            </div>
        </div>
    );
};

export default observer(ProfileInfoPage);
